import { reactive } from 'vue'
import { api } from '@/api'
import { getToken, setToken } from '@/api/http'
import { formatMoney } from '@/utils/money'
import { formatDateTime, todayIn, addDays } from '@/utils/dates'
import { startRealtime, stopRealtime } from './realtime'

// La seule source pour décider quels écrans afficher est GET /auth/me :
// utilisateur, permissions et organisation (devise, fuseau).
export const session = reactive({
  user: null,
  loaded: false,
  /** Vrai si le jeton stocké a été refusé au démarrage (affiche « session expirée »). */
  sessionExpired: false,

  get isAuthenticated() {
    return !!this.user
  },
  get permissions() {
    return this.user?.permissions ?? []
  },
  get organization() {
    return this.user?.organization ?? null
  },
  get currency() {
    return this.organization?.currency ?? ''
  },
  get timezone() {
    return this.organization?.timezone ?? undefined
  },

  can(permission) {
    return this.permissions.includes(permission)
  },
  canAny(permissions) {
    return permissions.some((p) => this.permissions.includes(p))
  },

  money(value, options) {
    return formatMoney(value, this.currency, options)
  },
  dateTime(value) {
    return formatDateTime(value, this.timezone)
  },
  today() {
    return todayIn(this.timezone)
  },
  /** Une date métier ne peut pas dépasser demain, dans le fuseau de l'organisation. */
  maxBusinessDate() {
    return addDays(this.today(), 1)
  },

  /** Restaure la session au démarrage si un jeton est stocké. */
  async restore() {
    if (this.loaded) return
    if (getToken()) {
      try {
        this.user = await api.auth.me()
        startRealtime()
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          setToken(null)
          this.sessionExpired = true
        }
        // Erreur réseau : on garde le jeton, l'utilisateur pourra réessayer.
        this.user = null
      }
    }
    this.loaded = true
  },

  async login(payload) {
    await this.start(await api.auth.login(payload))
  },

  async register(payload) {
    await this.start(await api.auth.register(payload))
  },

  /** Enregistre le jeton reçu ; l'utilisateur vient de la réponse ou de /auth/me. */
  async start(data) {
    setToken(data.token)
    this.user = data.user || null
    if (!this.user) await this.refresh()
    this.loaded = true
    startRealtime()
  },

  async refresh() {
    this.user = await api.auth.me()
  },

  async logout({ everywhere = false } = {}) {
    try {
      await (everywhere ? api.auth.logoutAll() : api.auth.logout())
    } catch (e) {
      /* le jeton est peut-être déjà invalide : on vide la session de toute façon */
    }
    this.clear()
  },

  /** Vide la session locale (401, désactivation du compte, déconnexion). */
  clear() {
    stopRealtime()
    setToken(null)
    this.user = null
    this.loaded = true
  }
})
